import { useCallback } from "react";
import {
  OrganizationMessageCountsDocument,
  OrganizationMessageDetailDocument,
  OrganizationMessagesDocument,
  OrganizationMessageStatsDocument,
  type OrganizationMessageSeverity,
  usePublishOrganizationMessageMutation,
  useScheduleOrganizationMessageMutation,
} from "../../generated/graphql";
import { useConfirmDialog } from "../api-keys-admin/components/confirm-dialog";
import { throwOnGraphqlErrors } from "../api-keys-admin/throw-on-graphql-errors";
import {
  type AudienceState,
  audienceLabel,
  formatDateTime,
  type MessageReach,
  reachSummary,
} from "./message-content";

export const ALL_COMPANIES_KEYWORD = "SEND";

/** Refetches every mounted view of messages after a change. */
export const REFETCH_QUERIES = [
  OrganizationMessagesDocument,
  OrganizationMessageCountsDocument,
  OrganizationMessageDetailDocument,
  OrganizationMessageStatsDocument,
];

type SendTarget = AudienceState & {
  readonly severity: OrganizationMessageSeverity;
};

/**
 * Sending a draft, shared by the composer and the message actions: confirm
 * the audience and its reach (typing SEND for every company), then publish
 * now or schedule.
 */
const useSendMessage = () => {
  const { confirm, confirmDialog } = useConfirmDialog();
  const options = {
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
  };
  const [publishMessage] = usePublishOrganizationMessageMutation(options);
  const [scheduleMessage] = useScheduleOrganizationMessageMutation(options);

  /** The confirmation to send with, or `undefined` when staff cancelled. */
  const confirmSend = useCallback(
    async (
      target: SendTarget,
      reach: MessageReach | undefined,
      startsAt: Date | null,
    ): Promise<{ confirmation: string | null } | undefined> => {
      const to = target.allCompanies ? "every company" : audienceLabel(target);
      try {
        await confirm({
          title:
            startsAt === null
              ? `Send to ${to} now?`
              : `Schedule for ${to} on ${formatDateTime(startsAt)}?`,
          description:
            reach === undefined
              ? undefined
              : reachSummary(target.severity, reach),
          confirmationText: startsAt === null ? "Send now" : "Schedule",
          ...(target.allCompanies
            ? {
                confirmationKeyword: ALL_COMPANIES_KEYWORD,
                confirmationKeywordPlaceholder: `Type ${ALL_COMPANIES_KEYWORD} to confirm`,
              }
            : {}),
        });
      } catch {
        return undefined;
      }
      return {
        confirmation: target.allCompanies ? ALL_COMPANIES_KEYWORD : null,
      };
    },
    [confirm],
  );

  /** Publishes now, or schedules at `startsAt`; throws the server's errors. */
  const deliver = useCallback(
    async (
      uuid: string,
      confirmation: string | null,
      startsAt: Date | null,
    ): Promise<void> => {
      throwOnGraphqlErrors(
        startsAt === null
          ? await publishMessage({
              variables: { input: { uuid, confirmation } },
            })
          : await scheduleMessage({
              variables: { input: { uuid, startsAt, confirmation } },
            }),
      );
    },
    [publishMessage, scheduleMessage],
  );

  return { confirmSend, deliver, confirmDialog };
};

export default useSendMessage;
